import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles, Rocket, Users } from 'lucide-react';
import { companyInfo, products, whyDubai, heroImages } from '../data/mock';

const Home = () => {
  const [currentHeroImage, setCurrentHeroImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          {heroImages.map((img, index) => (
            <div
              key={img}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentHeroImage ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={img}
                alt="AI Technology"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
            </div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-white/90">Shaping the Future with AI</span>
          </div>
          
          <h1 className="text-7xl md:text-9xl font-extralight text-white mb-6 tracking-tight">
            AZB Solutions
          </h1>
          
          <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
            Where Intelligence Meets Imagination. We build AI-powered SaaS platforms that reshape industries and empower humanity.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button size="lg" className="bg-white text-black hover:bg-white/90 text-base px-8 py-6 rounded-full transition-all hover:scale-105">
                Explore Our Products
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/ideas">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-base px-8 py-6 rounded-full transition-all hover:scale-105">
                Share Your Vision
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-light text-black mb-6 tracking-tight">
                Innovation
                <br />
                <span className="text-purple-600">Beyond Boundaries</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Headquartered in Dubai, AZB Solutions is a next-generation technology company driven by a bold vision: to reshape the world through AI-powered innovation and the limitless potential of human creativity.
              </p>
              <Link to="/about">
                <Button variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-white transition-all rounded-full px-6">
                  Discover Our Story
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-3xl">
                <Rocket className="w-12 h-12 text-purple-600 mb-4" />
                <h3 className="text-2xl font-light mb-2">Innovation First</h3>
                <p className="text-sm text-gray-600">Creating tomorrow's technology today</p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 rounded-3xl mt-8">
                <Users className="w-12 h-12 text-gray-700 mb-4" />
                <h3 className="text-2xl font-light mb-2">Global Community</h3>
                <p className="text-sm text-gray-600">Building the future together</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light text-black mb-4 tracking-tight">
              Our <span className="text-purple-600">Intelligent</span> Platforms
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              AI-powered solutions that transform how businesses operate
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-3xl font-light text-white mb-2">{product.name}</h3>
                    <p className="text-white/80 text-sm">{product.tagline}</p>
                  </div>
                </div>
                <div className="p-8">
                  <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>
                  <Link to={`/products#${product.name.toLowerCase().replace('.', '')}`}>
                    <Button variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white transition-all rounded-full">
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Dubai */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={whyDubai.image}
                alt="Dubai Skyline"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/40 to-transparent"></div>
            </div>
            <div>
              <h2 className="text-5xl font-light text-black mb-6 tracking-tight">
                {whyDubai.title}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {whyDubai.content.substring(0, 400)}...
              </p>
              <Link to="/about#dubai">
                <Button variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-white transition-all rounded-full px-6">
                  Read More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-purple-600 via-purple-700 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-light mb-6 tracking-tight">
            Have a Vision?
            <br />
            Let's Build It Together
          </h2>
          <p className="text-xl text-white/80 mb-12 font-light">
            Join our global community of innovators. Share your ideas, and let's create the future.
          </p>
          <Link to="/ideas">
            <Button size="lg" className="bg-white text-purple-700 hover:bg-white/90 text-base px-8 py-6 rounded-full transition-all hover:scale-105">
              Share Your Idea
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;